import { useTranslation } from "@refinedev/core";

import { Alert, AlertTitle, Checkbox, Stack, Typography } from "@mui/material";

export interface EmailMfaNoPhoneWarningProps {
  /** Whether the user has acknowledged the no-phone warning */
  acknowledgedNoPhone: boolean;
  /** Callback when acknowledgment changes */
  onAcknowledgeChange: (checked: boolean) => void;
}

/**
 * Warning alert shown when enabling Email MFA without a verified phone number.
 * Informs the user they won't have phone recovery and requires acknowledgment.
 *
 * Used by both setup-mfa.tsx (login flow) and ChangeMFAPanel.tsx (profile page).
 */
export const EmailMfaNoPhoneWarning: React.FC<EmailMfaNoPhoneWarningProps> = ({
  acknowledgedNoPhone,
  onAcknowledgeChange,
}) => {
  const { translate } = useTranslation();

  return (
    <Alert
      severity="warning"
      component="div"
      sx={({ palette }) => {
        const alertBorder = palette.alertYellow?.dark ?? palette.warning.dark;
        return {
          m: 1,
          mt: 0,
          backgroundColor: palette.warning.light,
          border: `1px solid ${alertBorder}`,
          "& .MuiAlert-icon": {
            color: alertBorder,
          },
        };
      }}
    >
      <AlertTitle>{translate("pages.mfa.emailNoPhoneWarningTitle")}</AlertTitle>
      <Typography variant="caption" whiteSpace="pre-line" color="text.primary">
        {translate("pages.mfa.emailNoPhoneWarningBody")}
      </Typography>
      <Stack direction="row" alignItems="flex-start" mt={1}>
        <Checkbox
          checked={acknowledgedNoPhone}
          onChange={e => onAcknowledgeChange(e.target.checked)}
          onClick={e => e.stopPropagation()}
          size="small"
        />
        <Typography variant="caption" lineHeight={1.4} color="text.primary">
          {translate("pages.mfa.emailNoPhoneAcknowledge")}
        </Typography>
      </Stack>
    </Alert>
  );
};
